import React, { useEffect, useMemo, useCallback } from 'react';
import { useCalculations } from '../../context/CalculationContext';
import { useScenarios } from '../../context/ScenarioContext';
import { useSettings } from '../../context/SettingsContext';
import ChartDisplayOptions from './ChartDisplayOptions';
import {
  ResponsiveContainer,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Line,
  ComposedChart
} from 'recharts';

// Custom tooltip component for enhanced hover information
const CustomTooltip = ({ active, payload, label, formatCurrency, componentColors, scenarios, calculationResults }) => {
  if (!active || !payload || !payload.length) return null;
  
  const scenarioData = {};
  
  // Extract grade and step directly from the scenarios
  const gradeStepInfo = {};
  
  // Debug function disabled to prevent console loops
  const debug = (msg) => {
    // Debug logging disabled
  };
  
  debug(`Processing tooltip for year: ${label}`);
  debug(`Available scenarios: ${scenarios.map(s => s.id).join(', ')}`);
  
  // Directly get grade and step info for each scenario based on the current year
  scenarios.forEach(scenario => {
    // Try to get the grade and step for this scenario at this year (label)
    let grade = null;
    let step = null;
    
    // First try: Direct properties on scenario
    if (scenario.grade !== undefined && scenario.step !== undefined) {
      grade = scenario.grade;
      step = scenario.step;
      debug(`Scenario ${scenario.id}: found grade/step directly: ${grade}-${step}`);
    } 
    // Second try: currentGrade/currentStep or finalGrade/finalStep
    else if ((scenario.currentGrade !== undefined && scenario.currentStep !== undefined) ||
             (scenario.finalGrade !== undefined && scenario.finalStep !== undefined)) {
      grade = scenario.currentGrade || scenario.finalGrade;
      step = scenario.currentStep || scenario.finalStep;
      debug(`Scenario ${scenario.id}: found grade/step from current/final: ${grade}-${step}`);
    }
    // Third try: Get from calculation results
    else if (calculationResults && calculationResults.scenarioResults) {
      const results = calculationResults.scenarioResults[scenario.id];
      debug(`Looking in calculation results for scenario ${scenario.id}`);
      
      if (results) {
        // Attempt to find year data in results
        if (results.salaryData && Array.isArray(results.salaryData)) {
          // Find the index of the current year in the years array
          const yearIndex = results.years ? results.years.indexOf(label) : -1;
          if (yearIndex >= 0) {
            debug(`Found year ${label} at index ${yearIndex}`);
            
            // Use the year index to get grade/step if available
            if (results.gradeByYear && results.stepByYear) {
              grade = results.gradeByYear[yearIndex];
              step = results.stepByYear[yearIndex];
              debug(`Found grade/step by year index: ${grade}-${step}`);
            }
          }
        }
        
        // Try different properties where grade/step info might be stored
        if (!grade || !step) {
          if (results.gradeStepByYear && results.gradeStepByYear[label]) {
            grade = results.gradeStepByYear[label].grade;
            step = results.gradeStepByYear[label].step;
            debug(`Found in gradeStepByYear: ${grade}-${step}`);
          } else if (results.gsInfo && results.gsInfo[label]) {
            grade = results.gsInfo[label].grade;
            step = results.gsInfo[label].step;
            debug(`Found in gsInfo: ${grade}-${step}`);
          } else if (results.yearlyData && results.yearlyData[label]) {
            grade = results.yearlyData[label].grade;
            step = results.yearlyData[label].step;
            debug(`Found in yearlyData: ${grade}-${step}`);
          }
        }
        
        // Last resort: If this is a current year and we have current grade/step
        if (!grade || !step) {
          if (scenario.currentGrade && scenario.currentStep) {
            grade = scenario.currentGrade;
            step = scenario.currentStep;
            debug(`Using fallback currentGrade/Step: ${grade}-${step}`);
          }
        }
      }
    }
    
    // If we found grade and step info, store it
    if (grade !== null && step !== null) {
      gradeStepInfo[scenario.id] = { grade, step };
      debug(`Stored grade/step for scenario ${scenario.id}: ${grade}-${step}`);
    } else {
      debug(`Could not find grade/step for scenario ${scenario.id}`);
    }
  });
  
  // Initialize with all scenarios that have data
  payload.forEach(entry => {
    if (!entry.name) return;
    
    const parts = entry.name.split(' - ');
    if (parts.length === 2) {
      const scenarioName = parts[0];
      const componentName = parts[1];
      
      if (!scenarioData[scenarioName]) {
        scenarioData[scenarioName] = {
          name: scenarioName,
          components: [],
          totalIncome: 0
        };
      }
      
      // Skip $0 values
      if (entry.value === 0) return;
      
      // Skip 'Income Delta' since it's handled separately
      if (componentName.includes('Income Delta')) return;
      
      // Find the scenario object that matches this name
      const matchingScenario = scenarios.find(s => s.name === scenarioName);
      const scenarioId = matchingScenario ? matchingScenario.id : null;
      
      // If this is the Total Income component, store it separately
      if (componentName === 'Total Income') {
        scenarioData[scenarioName].totalIncome = entry.value;
      }
      
      // For the Salary component, also store grade-step info if available
      if (componentName === 'Salary' && scenarioId && gradeStepInfo[scenarioId]) {
        scenarioData[scenarioName].gradeStep = gradeStepInfo[scenarioId];
        debug(`Added grade/step to scenario data for ${scenarioName}`);
      }
      
      scenarioData[scenarioName].components.push({
        name: componentName,
        value: entry.value,
        color: entry.color || entry.stroke || componentColors[componentName] || '#888'
      });
    }
  });
  
  return (
    <div style={{
      background: 'rgba(0, 0, 0, 0.85)',
      border: '1px solid #ccc',
      padding: '10px',
      borderRadius: '4px',
      boxShadow: '0 2px 4px rgba(0, 0, 0, 0.2)',
      color: 'white',
      minWidth: '200px',
      maxWidth: '300px'
    }}>
      <div style={{ marginBottom: '6px', fontWeight: 'bold', color: '#fff' }}>
        Year: {label}
      </div>
      <div style={{ margin: '4px 0' }}>
        {/* Map through each scenario's data */}
        {Object.values(scenarioData).map(data => {
          const { name: scenarioName, components } = data;
          
          // Sort components by value (descending) and filter out Total Income
          const sortedComponents = [...components]
            .filter(comp => comp.name !== 'Total Income')
            .sort((a, b) => b.value - a.value);
        
          return (
            <div key={scenarioName} style={{ marginBottom: '8px' }}>
              <div style={{ 
                fontWeight: 'bold', 
                marginBottom: '4px',
                color: components.find(c => c.name === 'Total Income')?.color || 'white' 
              }}>
                {scenarioName}
              </div>
              {sortedComponents.map((component, index) => (
                <div key={index} style={{ 
                  display: 'flex', 
                  justifyContent: 'space-between',
                  margin: '2px 0'
                }}>
                  <div style={{ 
                    display: 'flex',
                    alignItems: 'center'
                  }}>
                    <div style={{
                      width: '10px',
                      height: '10px',
                      backgroundColor: component.color,
                      marginRight: '5px',
                      borderRadius: '2px'
                    }} />
                    {component.name === 'Salary' && data.gradeStep ? 
                      `Salary (GS ${data.gradeStep.grade}-${data.gradeStep.step}):` : 
                      `${component.name}:`
                    }
                  </div>
                  <div style={{ fontWeight: 'bold' }}>{formatCurrency(component.value)}</div>
                </div>
              ))}
              
              {/* Show total income if available */}
              {data.totalIncome > 0 && (
                <div style={{ 
                  display: 'flex', 
                  justifyContent: 'space-between',
                  margin: '4px 0',
                  borderTop: '1px solid rgba(255,255,255,0.2)',
                  paddingTop: '4px',
                  fontWeight: 'bold'
                }}>
                  <div>Total Income:</div>
                  <div>{formatCurrency(data.totalIncome)}</div>
                </div>
              )}
            </div>
          );
        })}
        
        {/* Show Income Delta if it exists */}
        {payload.some(entry => entry.name && entry.name.includes('Income Delta')) && (
          <div style={{ 
            marginTop: '8px',
            borderTop: '1px dashed #ccc',
            paddingTop: '8px'
          }}>
            <div style={{ fontWeight: 'bold', marginBottom: '4px', color: '#f8f8f8' }}>
              Income Difference
            </div>
            {payload
              .filter(entry => entry.name && entry.name.includes('Income Delta'))
              .map((entry, index) => (
                <div key={index} style={{ 
                  display: 'flex', 
                  justifyContent: 'space-between',
                  margin: '2px 0'
                }}>
                  <div style={{ 
                    display: 'flex',
                    alignItems: 'center'
                  }}>
                    <div style={{
                      width: '10px',
                      height: '10px',
                      backgroundColor: entry.color || entry.stroke || '#888',
                      marginRight: '5px',
                      borderRadius: '2px'
                    }} />
                    {entry.name}:
                  </div>
                  <div style={{ fontWeight: 'bold' }}>
                    {formatCurrency(entry.value)}
                  </div>
                </div>
              ))
            }
          </div>
        )}
      </div>
    </div>
  );
};

const ResultsChart = () => {
  // Function to format Y-axis ticks concisely
  const formatYAxisTickValue = (value) => {
    if (value >= 1000000) {
      return `$${(value / 1000000).toFixed(1).replace(/\.0$/, '')}M`;
    } else if (value >= 1000) {
      return `$${(value / 1000).toFixed(0)}k`;
    } else {
      return `$${value}`;
    }
  };

  const { calculationResults, isCalculating } = useCalculations(); // Get loading state
  const { scenarios } = useScenarios();
  const { scenarioVisibility, initScenarioVisibility, showIncomeDelta, toggleSection, expandedSections } = useSettings(); // Added showIncomeDelta, globalSettings, toggleSection, and expandedSections

  // Initialize visibility settings for each scenario
  useEffect(() => {
    scenarios.forEach(scenario => {
      initScenarioVisibility(scenario.id);
    });
  }, [scenarios, initScenarioVisibility]);

  // Filter out scenarios with no results
  const visibleScenarios = scenarios.filter(s => 
    s.visible !== false && 
      calculationResults.scenarioResults[s.id] &&
      calculationResults.scenarioResults[s.id].years?.length > 0
  );
  
  
  // Define component colors for stacked area chart with good contrast and visibility
  const componentColors = {
    'Salary': '#3b82f6',        // Blue
    'Pension': '#6366f1',       // Indigo
    'TSP': '#8b5cf6',           // Purple
    'Supplement': '#a78bfa',    // Light Purple
    'Social Security': '#c4b5fd', // Lavender
    'Additional Income': '#a0aec0', // Slate Gray - new
    'Total Income': '#000000'   // Black for Total Income
  };
  
  // Define stroke colors (slightly darker versions of the fill colors)
  const componentStrokeColors = {
    'Salary': '#2563eb',        // Darker Blue
    'Pension': '#4f46e5',       // Darker Indigo
    'TSP': '#7c3aed',           // Darker Purple
    'Supplement': '#8b5cf6',    // Darker Light Purple
    'Social Security': '#a78bfa', // Darker Lavender
    'Additional Income': '#718096', // Darker Slate Gray - new
    'Total Income': '#000000'   // Black for Total Income
  };

  // Define a distinct color palette for scenarios - static array, no need to memoize
  const scenarioColorPalette = [
    '#1f77b4',  // Muted Blue
    '#ff7f0e',  // Safety Orange
    '#2ca02c',  // Cooked Asparagus Green
    '#d62728',  // Brick Red
    '#9467bd',  // Muted Purple
    '#8c564b',  // Chestnut Brown
    '#e377c2',  // Raspberry Sorbet Pink
    '#7f7f7f',  // Middle Gray
    '#bcbd22',  // Curry Yellow-Green
    '#17becf'   // Blue-Teal
  ];
  
  // Memoize scenario colors to avoid recalculation on every render
  const scenarioColors = useMemo(() => {
    const colorMap = {};
    visibleScenarios.forEach((scenario, index) => {
      colorMap[scenario.id] = scenarioColorPalette[index % scenarioColorPalette.length];
    });
    return colorMap;
  }, [visibleScenarios]);
  
  // Format currency helper function
  const formatCurrency = useCallback((value) => {
    if (value === null || value === undefined || isNaN(value)) return 'N/A';
    return value.toLocaleString('en-US', { 
      style: 'currency', 
      currency: 'USD', 
      maximumFractionDigits: 0 
    });
  }, []);



  // 1. First, prepare the chart data
  const chartData = useMemo(() => {
    if (!visibleScenarios.length) return [];
    
    const chartData = [];
    const firstScenarioId = visibleScenarios[0]?.id;
    const scenarioResults = calculationResults.scenarioResults[firstScenarioId];
    
    // Use first scenario's years as the base
    if (!scenarioResults || !scenarioResults.years || scenarioResults.years.length === 0) {
      return [];
    }
    
    // Create a data point for each year
    scenarioResults.years.forEach((year, index) => {
      // Ensure year is treated as a number, not a string
      const dataPoint = { year: Number(year) };
      
      // Add data from each visible scenario
      visibleScenarios.forEach(scenario => {
        const results = calculationResults.scenarioResults[scenario.id];
        if (!results) return;
        
        // Use scenario-specific visibility settings (not global visibilitySettings)
        // as per requirements in the memory
        const prefix = scenario.name;
        const scenarioVis = scenarioVisibility[scenario.id] || {};
        
        if (scenarioVis.showSalary && results.salaryData) {
          dataPoint[`${prefix} - Salary`] = results.salaryData[index] || 0;
        }
        
        if (scenarioVis.showPension && results.pensionData) {
          dataPoint[`${prefix} - Pension`] = results.pensionData[index] || 0;
        }
        
        // TSP withdrawals - calculated from balance
        if (scenarioVis.showTsp && results.tspBalanceData) {
          const tspBalance = results.tspBalanceData ? results.tspBalanceData[index] || 0 : 0;
          const isRetirementYear = index >= (results.years ? results.years.indexOf(scenario.retirementYear) : -1);
          const tspWithdrawal = isRetirementYear ? (tspBalance * (scenario.withdrawalRate / 100)) : 0;
          dataPoint[`${prefix} - TSP`] = tspWithdrawal;
        }
        
        if (scenarioVis.showTSPBalance && results.tspBalanceData) {
          dataPoint[`${prefix} - TSP Balance`] = results.tspBalanceData[index] || 0;
        }
        
        if (scenarioVis.showFERSSupplement && results.supplementData) {
          dataPoint[`${prefix} - Supplement`] = results.supplementData[index] || 0;
        }
        
        if (scenarioVis.showSocialSecurity && results.socialSecurityData) {
          dataPoint[`${prefix} - Social Security`] = results.socialSecurityData[index] || 0;
        }

        if (scenarioVis.showAdditionalIncome && results.additionalIncomeData) { // New: Add Additional Income to chart data
          dataPoint[`${prefix} - Additional Income`] = results.additionalIncomeData[index] || 0;
        }
        
        // Calculate Total Income for this scenario and year based on ALL components
        let totalIncome = 0;
        
        // Always add all income components regardless of visibility settings
        if (results.salaryData) totalIncome += (results.salaryData[index] || 0);
        if (results.pensionData) totalIncome += (results.pensionData[index] || 0);
        
        // Add TSP withdrawal only in retirement years, regardless of visibility
        if (results.tspBalanceData) {
          const tspBalance = results.tspBalanceData[index] || 0;
          const isRetirementYear = index >= (results.years ? results.years.indexOf(scenario.retirementYear) : -1);
          totalIncome += isRetirementYear ? (tspBalance * (scenario.withdrawalRate / 100)) : 0;
        }
        
        if (results.supplementData) {
          totalIncome += (results.supplementData[index] || 0);
        }
        
        if (results.socialSecurityData) {
          totalIncome += (results.socialSecurityData[index] || 0);
        }

        if (results.additionalIncomeData) { // New: Add Additional Income to total income calculation
          totalIncome += (results.additionalIncomeData[index] || 0);
        }
        
        // Store the total income for this scenario in this year
        // But only make it visible in the chart when toggled on
        dataPoint[`${prefix} - Total Income`] = totalIncome;
      });
      
      // Calculate Income Delta if showing income delta and have at least 2 scenarios
      if (showIncomeDelta && visibleScenarios.length >= 2) {
        const scen1 = visibleScenarios[0];
        const scen2 = visibleScenarios[1];
        
        if (scen1 && scen2) {
          const results1 = calculationResults.scenarioResults[scen1.id];
          const results2 = calculationResults.scenarioResults[scen2.id];
          
          if (results1 && results2) {
            const total1 = dataPoint[`${scen1.name} - Total Income`] || 0;
            const total2 = dataPoint[`${scen2.name} - Total Income`] || 0;
            dataPoint.incomeDelta = total2 - total1; // Scenario 2 - Scenario 1
          }
        }
      }
      
      chartData.push(dataPoint);
    });
    
    return chartData;
  }, [visibleScenarios, calculationResults, scenarioVisibility, showIncomeDelta]);
  
  // Track if we have valid data points to display
  const hasDataPoints = chartData.length > 0 && Object.keys(chartData[0]).length > 1; // Check if more than just 'year' exists
  
  // For debugging - log the chart data structure
  // Chart data is ready for rendering
  
  // 2. Then, prepare the datasets for Chart.js using the chartData (kept for compatibility)
  const chartDatasets = useMemo(() => {
    // Array to hold all datasets
    const allDatasets = [];

    // First, create Income Delta dataset if enabled
    if (showIncomeDelta && visibleScenarios.length >= 2) {
      const scen1Id = visibleScenarios[0]?.id;
      const scen2Id = visibleScenarios[1]?.id;
      const results1 = calculationResults.scenarioResults[scen1Id];
      const results2 = calculationResults.scenarioResults[scen2Id];

      if (results1 && results2 && results1.totalIncomeData && results2.totalIncomeData && results1.years?.length === results2.years?.length) {
        const deltaData = results1.years.map((year, index) => {
          const income1 = results1.totalIncomeData[index] || 0;
          const income2 = results2.totalIncomeData[index] || 0;
          return income2 - income1;
        });

        allDatasets.push({
          type: 'line', // This will be a line, not stacked
          label: `Income Delta (${visibleScenarios[1].name} - ${visibleScenarios[0].name})`,
          data: deltaData,
          borderColor: '#888888', // Grey color
          backgroundColor: 'rgba(136, 136, 136, 0.2)',
          borderDash: [5, 5], // Dashed line
          borderWidth: 2,
          tension: 0.1,
          pointRadius: 2,
          fill: false,
          order: -1 // Display on top of stacked areas
        });
      }
    }

    // Process each visible scenario
    visibleScenarios.forEach(scenario => {
      const results = calculationResults.scenarioResults[scenario.id];
      if (!results || !results.years) return;

      const prefix = scenario.name;
      const scenarioVis = scenarioVisibility[scenario.id] || {};
      
      // For stacked area chart, we'll group the data components together
      const componentsDatasets = [];
      
      // Define the component order for consistent stacking
      const componentOrder = [
        { type: 'Salary', show: scenarioVis.showSalary, data: results.salaryData, color: componentColors['Salary'] },
        { type: 'Pension', show: scenarioVis.showPension, data: results.pensionData, color: componentColors['Pension'] },
        { type: 'TSP', show: scenarioVis.showTsp, data: null, color: componentColors['TSP'] },
        { type: 'Supplement', show: scenarioVis.showFERSSupplement, data: results.supplementData, color: componentColors['Supplement'] },
        { type: 'Social Security', show: scenarioVis.showSocialSecurity, data: results.socialSecurityData, color: componentColors['Social Security'] },
      ];

      // Process each component in order for stacking
      componentOrder.forEach(component => {
        if (!component.show) return;
        
        let componentData;
        
        // Special handling for TSP which is calculated from balance
        if (component.type === 'TSP' && results.tspBalanceData) {
          componentData = results.years.map((year, index) => {
            const tspBalance = results.tspBalanceData ? results.tspBalanceData[index] || 0 : 0;
            const isRetirementYear = index >= (results.years ? results.years.indexOf(scenario.retirementYear) : -1);
            return isRetirementYear ? (tspBalance * (scenario.withdrawalRate / 100)) : 0;
          });
        } else {
          componentData = component.data;
        }

        if (!componentData) return;

        // Add this component to the stack group
        componentsDatasets.push({
          type: 'line',  // Use line type with fill for stacked area chart
          label: `${prefix} - ${component.type}`,
          data: componentData,
          borderColor: componentStrokeColors[component.type],
          backgroundColor: component.color,
          borderWidth: 1,
          pointRadius: 0,
          pointHoverRadius: 3,
          tension: 0.1,
          fill: true,
          // Make hit detection area larger to improve tooltip triggering on hover
          segment: {
            backgroundColor: component.color,
            borderColor: componentStrokeColors[component.type],
          },
          // Increase element detection radius for better hover interaction
          hitRadius: 10,
          // Link to the scenario's stack group
          stack: `stack-${scenario.id}`,
          order: 1
        });
      });

      // Special handling for TSP Balance - not part of the stack, separate line
      if (scenarioVis.showTSPBalance && results.tspBalanceData) {
        allDatasets.push({
          type: 'line',
          label: `${prefix} - TSP Balance`,
          data: results.tspBalanceData,
          borderColor: scenarioColors[scenario.id],
          backgroundColor: 'rgba(0, 0, 0, 0)', // Transparent
          borderDash: [5, 2],
          borderWidth: 2,
          pointRadius: 0,
          pointHoverRadius: 3,
          tension: 0.1,
          fill: false,
          yAxisID: 'y-balance', // Use a separate y-axis for TSP Balance
          order: 0 // Display on top
        });
      }

      // Always calculate Total Income with ALL components regardless of visibility settings
      const totalIncome = results.years.map((year, index) => {
        let total = 0;
        
        // Include all income components regardless of visibility
        if (results.salaryData) total += (results.salaryData[index] || 0);
        if (results.pensionData) total += (results.pensionData[index] || 0);
        
        // Include TSP withdrawals but only during retirement years
        if (results.tspBalanceData) {
          const tspBalance = results.tspBalanceData[index] || 0;
          const isRetirementYear = index >= (results.years ? results.years.indexOf(scenario.retirementYear) : -1);
          total += isRetirementYear ? (tspBalance * (scenario.withdrawalRate / 100)) : 0;
        }
        
        // Include FERS Supplement and Social Security
        if (results.supplementData) total += (results.supplementData[index] || 0);
        if (results.socialSecurityData) total += (results.socialSecurityData[index] || 0);
        
        return total;
      });
      
      // Add Total Income as a prominent line, with only its visibility controlled by the toggle
      allDatasets.push({
        type: 'line',
        label: `${prefix} - Total Income`,
        data: totalIncome,
        borderColor: scenarioColors[scenario.id],
        backgroundColor: 'rgba(0, 0, 0, 0)', // Transparent
        borderWidth: 2,
        pointRadius: 0,
        pointHoverRadius: 4,
        tension: 0.1,
        fill: false,
        order: -10, // Ensure it's always on top (lowest order value)
        hidden: !scenarioVis.showTotal // Control visibility through this property instead of conditional rendering
      });
      
      // Add all component datasets to the main datasets array
      allDatasets.push(...componentsDatasets);
    });
    
    return allDatasets;
  }, [visibleScenarios, calculationResults, scenarioVisibility, scenarioColors, showIncomeDelta]);

  // 3. Chart options configuration with tooltips, scales, and other settings
  const chartOptions = useMemo(() => {
    return {
      responsive: true,
      maintainAspectRatio: false,
      interaction: {
        mode: 'nearest',
        intersect: false,
        axis: 'x'
      },
      scales: {
        x: {
          title: {
            display: true,
            text: 'Year',
            font: {
              size: 14,
              weight: 'bold'
            }
          },
          ticks: {
            maxRotation: 0
          }
        },
        y: {
          position: 'left',
          title: {
            display: true,
            text: 'Income (USD)',
            font: {
              size: 14,
              weight: 'bold'
            }
          },
          beginAtZero: true,
          ticks: {
            callback: function(value) {
              return value.toLocaleString('en-US', {
                style: 'currency',
                currency: 'USD',
                maximumFractionDigits: 0
              });
            }
          }
        },
        'y-balance': {
          position: 'right',
          title: {
            display: true,
            text: 'TSP Balance (USD)',
            font: {
              size: 14,
              weight: 'bold'
            }
          },
          beginAtZero: true,
          grid: {
            drawOnChartArea: false // only show grid lines for primary y-axis
          },
          ticks: {
            callback: function(value) {
              return value.toLocaleString('en-US', {
                style: 'currency',
                currency: 'USD',
                maximumFractionDigits: 0
              });
            }
          }
        }
      },
      plugins: {
        tooltip: {
          enabled: true,
          backgroundColor: 'rgba(0,0,0,0.9)',
          titleColor: '#FFFFFF',
          bodyColor: '#FFFFFF',
          footerColor: '#FFFFFF',
          borderColor: '#DDDDDD',
          borderWidth: 1,
          padding: 10,
          // Configuration to show tooltips when hovering over filled areas, not just lines
          mode: 'index',
          intersect: false,
          // Tooltip performance optimizations
          animation: {
            duration: 50 // Very fast tooltip animations
          },
          position: 'nearest',
          displayColors: true,
          boxPadding: 6,
          usePointStyle: true,
          filter: function(tooltipItem) {
            const chart = tooltipItem.chart;
            const tooltip = chart.tooltip;
            
            if (!tooltip || !tooltip._active || tooltip._active.length === 0) {
              // console.log('Filter: No _active elements or tooltip, returning false.');
              return false; 
            }

            const mouseY = tooltip.y; // Y-pixel of the tooltip (approximates mouse Y)
            // console.log(`Filter: mouseY = ${mouseY}, _active elements count: ${tooltip._active.length}`);
            let bestMatchDatasetIndex = -1;

            // Attempt to match Total Income line by strict proximity first
            for (const activeEl of tooltip._active) {
                const currentDatasetIndex = activeEl.datasetIndex;
                const datasetLabel = chart.data.datasets[currentDatasetIndex].label || '';
                if (datasetLabel.includes('Total Income')) {
                    const elTopY = activeEl.element.y;
                    if (typeof elTopY !== 'undefined') {
                        const threshold = 5; // pixels proximity for precise line selection
                        // console.log(`  Proximity Check for Total Income: '${datasetLabel}' (idx ${currentDatasetIndex}), elTopY: ${elTopY}, mouseY: ${mouseY}`);
                        if (Math.abs(mouseY - elTopY) <= threshold) {
                            // console.log(`    PROXIMITY MATCH Total Income line: '${datasetLabel}'`);
                            bestMatchDatasetIndex = currentDatasetIndex;
                            break;
                        }
                    }
                }
            }

            // If no strict proximity match for Total Income, proceed with band checking for all elements
            if (bestMatchDatasetIndex === -1) {
                for (let i = 0; i < tooltip._active.length; i++) {
                    const activeEl = tooltip._active[i];
                    const currentDatasetIndex = activeEl.datasetIndex;
                    const datasetLabel = chart.data.datasets[currentDatasetIndex].label || '';
                    const elTopY = activeEl.element.y;

                    if (typeof elTopY === 'undefined') {
                        // console.log(`  Band Check: Skipping ${datasetLabel} (idx ${currentDatasetIndex}) due to undefined elTopY`);
                        continue;
                    }

                    let elBottomY;
                    if (i + 1 < tooltip._active.length) {
                        const nextActiveEl = tooltip._active[i+1];
                        if (nextActiveEl.element && typeof nextActiveEl.element.y !== 'undefined') {
                            elBottomY = nextActiveEl.element.y; // Bottom is top of the next element
                            // console.log(`    Derived elBottomY from nextActiveEl (${chart.data.datasets[nextActiveEl.datasetIndex].label || 'Unknown'}): ${elBottomY}`);
                        } else {
                            elBottomY = chart.chartArea.bottom; // Fallback
                            // console.log(`    Fallback elBottomY for ${datasetLabel}: chart.chartArea.bottom = ${elBottomY}. nextActiveEl.element was ${JSON.stringify(nextActiveEl.element)}, nextActiveEl.element.y was ${nextActiveEl.element ? nextActiveEl.element.y : 'N/A'}`);
                        }
                    } else {
                        // This is the visually last element in the stack at this x-index
                        elBottomY = chart.chartArea.bottom;
                    }
                    
                    // console.log(`  Band Check for '${datasetLabel}' (idx ${currentDatasetIndex}): elTopY: ${elTopY}, elBottomY: ${elBottomY}, mouseY: ${mouseY}`);
                    // Ensure elTopY is not below elBottomY (can happen with line order)
                    // And that mouseY is within this calculated band
                    if (elTopY <= elBottomY && mouseY >= elTopY && mouseY <= elBottomY) {
                         // console.log(`    BAND MATCH for '${datasetLabel}'`);
                        bestMatchDatasetIndex = currentDatasetIndex;
                        break; 
                    }
                }
            }
            
            // console.log(`Filter: Final bestMatchDatasetIndex = ${bestMatchDatasetIndex}. Comparing with tooltipItem.datasetIndex = ${tooltipItem.datasetIndex} for item '${tooltipItem.dataset.label}'`);
            const shouldShow = tooltipItem.datasetIndex === bestMatchDatasetIndex;
            // if(shouldShow) console.log('Filter: ALLOWING', tooltipItem.dataset.label);
            // else console.log('Filter: BLOCKING', tooltipItem.dataset.label);
            return shouldShow;
          },
          callbacks: {
            title: function(context) {
              if (!context || context.length === 0) return '';
              return `Year: ${context[0].label}`;
            },
            label: function(context) {
              const chart = context.chart;
              const datasetLabel = context.dataset.label || '';
              const rawValue = context.raw;
              const dataIndex = context.dataIndex;

              const labelParts = datasetLabel.split(' - ');
              const hoveredScenarioName = labelParts[0];
              const componentType = labelParts.length > 1 ? labelParts[1] : '';

              const formatValue = (value) => {
                if (typeof value !== 'number' || isNaN(value)) return 'N/A';
                return value.toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 });
              };

              if (componentType === 'Total Income') {
                let scenario1Total = null;
                let scenario2Total = null;
                
                const scenario1Name = (scenarios.length > 0 && scenarios[0]) ? scenarios[0].name : 'Scenario 1';
                const scenario2Name = (scenarios.length > 1 && scenarios[1]) ? scenarios[1].name : null;

                const datasets = chart.data.datasets;
                datasets.forEach(dataset => {
                  if (dataset.label === `${scenario1Name} - Total Income`) {
                    scenario1Total = dataset.data[dataIndex];
                  }
                  if (scenario2Name && dataset.label === `${scenario2Name} - Total Income`) {
                    scenario2Total = dataset.data[dataIndex];
                  }
                });

                const tooltipLines = [];
                if (scenario1Total !== null) {
                  tooltipLines.push(`${scenario1Name} Total: ${formatValue(scenario1Total)}`);
                }

                if (scenario2Name && scenario2Total !== null) {
                  tooltipLines.push(`${scenario2Name} Total: ${formatValue(scenario2Total)}`);
                  if (scenario1Total !== null) { 
                    const difference = scenario1Total - scenario2Total;
                    tooltipLines.push(`Difference: ${formatValue(difference)}`);
                  }
                } else if (!scenario2Name && scenario1Total !== null && scenarios.length === 1) {
                  // If only one scenario is active and its total is found, tooltipLines will have one entry.
                  // If more specific behavior is needed for single scenario, this is the place.
                }
                
                if (tooltipLines.length > 0) {
                  return tooltipLines;
                } else {
                  // Fallback if specific totals weren't found (e.g. only one scenario's total income line exists but isn't S1 by name, or scenarios array is empty)
                  return `${hoveredScenarioName} ${componentType}: ${formatValue(rawValue)}`;
                }

              } else { // For other components (Salary, Pension, etc.)
                if (componentType) { 
                  return `${componentType}: ${formatValue(rawValue)}`;
                } else { 
                  return `${datasetLabel}: ${formatValue(rawValue)}`; // Fallback if no componentType
                }
              }
            },
            labelColor: function(context) {
              const label = context.dataset.label || '';
              const labelParts = label.split(' - ');
              const componentType = labelParts.length > 1 ? labelParts[1] : '';
              
              let bgColor = context.dataset.borderColor; // Default to line color for the box

              // componentColors is assumed to be in scope from useMemo's dependencies
              if (componentType && componentColors[componentType]) {
                bgColor = componentColors[componentType]; 
              } else if (componentType === 'Total Income' || componentType === 'TSP Balance') {
                bgColor = context.dataset.borderColor;
              } else if (context.dataset.backgroundColor && context.dataset.backgroundColor !== 'rgba(0,0,0,0)' && context.dataset.backgroundColor !== 'transparent' && !componentColors[componentType]) {
                  bgColor = context.dataset.backgroundColor;
              }
              
              return {
                borderColor: context.dataset.borderColor, 
                backgroundColor: bgColor 
              };
            }
          }
        },
        legend: {
          display: false // Disable default legend, custom controls are in ChartDisplayOptions
        }
      }
    }
  }, [visibleScenarios, scenarioVisibility, componentColors]);
  
  return (
    <div>
      {/* Chart Section */}
      <div className="chart-section" style={{ height: '500px', width: '100%', marginBottom: '30px' }}>
        {hasDataPoints ? (
        <>
          <h3 style={{ textAlign: 'center', marginBottom: '20px' }}>Retirement Income Projections</h3>
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart 
              data={chartData}
              margin={{ top: 10, right: 15, left: 0, bottom: 5 }}
            >
              <CartesianGrid strokeDasharray="3 3" opacity={0.6} />
              <XAxis 
                dataKey="year" 
                label={{ value: 'Year', position: 'insideBottomRight', offset: -5 }}
                allowDuplicatedCategory={false}
              />
              <YAxis 
                yAxisId="left"
                tickFormatter={formatYAxisTickValue}
                width={55}
              />
              <YAxis 
                yAxisId="right" 
                orientation="right" 
                label={{ value: 'TSP Balance (USD)', angle: 90, position: 'insideRight' }} 
                tickFormatter={formatYAxisTickValue}
                width={55}
              />
              <Tooltip 
                content={(props) => (
                  <CustomTooltip 
                    {...props}
                    scenarios={scenarios} 
                    calculationResults={calculationResults}
                    formatCurrency={formatCurrency} 
                    componentColors={componentColors} 
                  />
                )}
              />
              {/* Generate all components by iterating through scenarios */}
              {visibleScenarios.map(scenario => {
                const scenarioId = scenario.id;
                const scenarioName = scenario.name;
                const scenarioVis = scenarioVisibility[scenarioId] || {};
                const scenarioColor = scenarioColors[scenarioId];
                
                // Create an array of components to render for this scenario
                const componentConfigs = [
                  // Stacked area components
                  {
                    type: 'Area',
                    visible: scenarioVis.showSalary,
                    dataKey: `${scenarioName} - Salary`,
                    name: `${scenarioName} - Salary`,
                    fill: componentColors['Salary'],
                    stroke: componentStrokeColors['Salary'],
                    stackId: `stack-${scenarioId}`,
                    yAxisId: 'left'
                  },
                  {
                    type: 'Area',
                    visible: scenarioVis.showPension,
                    dataKey: `${scenarioName} - Pension`,
                    name: `${scenarioName} - Pension`,
                    fill: componentColors['Pension'],
                    stroke: componentStrokeColors['Pension'],
                    stackId: `stack-${scenarioId}`,
                    yAxisId: 'left'
                  },
                  {
                    type: 'Area',
                    visible: scenarioVis.showTsp,
                    dataKey: `${scenarioName} - TSP`,
                    name: `${scenarioName} - TSP`,
                    fill: componentColors['TSP'],
                    stroke: componentStrokeColors['TSP'],
                    stackId: `stack-${scenarioId}`,
                    yAxisId: 'left'
                  },
                  {
                    type: 'Area',
                    visible: scenarioVis.showFERSSupplement,
                    dataKey: `${scenarioName} - Supplement`,
                    name: `${scenarioName} - Supplement`,
                    fill: componentColors['Supplement'],
                    stroke: componentStrokeColors['Supplement'],
                    stackId: `stack-${scenarioId}`,
                    yAxisId: 'left'
                  },
                  {
                    type: 'Area',
                    visible: scenarioVis.showSocialSecurity,
                    dataKey: `${scenarioName} - Social Security`,
                    name: `${scenarioName} - Social Security`,
                    fill: componentColors['Social Security'],
                    stroke: componentStrokeColors['Social Security'],
                    stackId: `stack-${scenarioId}`,
                    yAxisId: 'left'
                  },
                  {
                    type: 'Area',
                    visible: scenarioVis.showAdditionalIncome, // New: Visibility for Additional Income
                    dataKey: `${scenarioName} - Additional Income`,
                    name: `${scenarioName} - Additional Income`,
                    fill: componentColors['Additional Income'],
                    stroke: componentStrokeColors['Additional Income'],
                    stackId: `stack-${scenarioId}`,
                    yAxisId: 'left'
                  },
                  // Line components
                  {
                    type: 'Line',
                    visible: scenarioVis.showTSPBalance,
                    dataKey: `${scenarioName} - TSP Balance`,
                    name: `${scenarioName} - TSP Balance`,
                    stroke: scenarioColor,
                    strokeWidth: 2,
                    strokeDasharray: '5 2',
                    dot: false,
                    activeDot: { r: 4 },
                    yAxisId: 'right',
                    fill: 'none'
                  },
                  {
                    type: 'Line',
                    visible: scenarioVis.showTotal,
                    dataKey: `${scenarioName} - Total Income`,
                    name: `${scenarioName} - Total Income`,
                    stroke: scenarioColor,
                    strokeWidth: 3,
                    dot: false,
                    activeDot: { r: 6 },
                    yAxisId: 'left',
                    fill: 'none'
                  }
                ];
                
                // Render only the visible components
                return componentConfigs
                  .filter(config => config.visible)
                  .map((config, index) => {
                    const key = `${scenarioId}-${config.dataKey}-${index}`;
                    
                    if (config.type === 'Area') {
                      return (
                        <Area 
                          key={key}
                          type="monotone"
                          dataKey={config.dataKey}
                          name={config.name}
                          fill={config.fill}
                          stroke={config.stroke}
                          stackId={config.stackId}
                          yAxisId={config.yAxisId}
                        />
                      );
                    } else {
                      return (
                        <Line 
                          key={key}
                          type="monotone"
                          dataKey={config.dataKey}
                          name={config.name}
                          stroke={config.stroke}
                          strokeWidth={config.strokeWidth}
                          strokeDasharray={config.strokeDasharray}
                          dot={config.dot}
                          activeDot={config.activeDot}
                          yAxisId={config.yAxisId}
                          fill={config.fill}
                        />
                      );
                    }
                  });
              })}
              
              
              {/* Income Delta (if enabled and have at least 2 scenarios) */}
              {showIncomeDelta && visibleScenarios.length >= 2 && (
                <Line 
                  yAxisId="left"
                  type="monotone"
                  dataKey="incomeDelta"
                  name="Income Delta"
                  stroke="#888888"
                  strokeWidth={2}
                  strokeDasharray="5 5"
                  dot={false}
                  activeDot={{ r: 4 }}
                  fill="none"
                />
              )}
            </ComposedChart>
          </ResponsiveContainer>
        </>
      ) : (
        <p className="no-data-message">
          {isCalculating ? "Calculating results..." : 
          visibleScenarios.length === 0 ? "No scenarios are marked as visible." : 
          !hasDataPoints ? "No data points to display. Your calculations may be returning zero values." :
          "No results data available. Check inputs or scenario settings."}
        </p>
      )}
    </div>

    {/* Chart Display Options */}
    {/* Chart Display Options (Expandable) */}
    <div className="chart-controls-section" style={{ 
      marginTop: '15px',
      border: '1px solid #eaeaea',
      borderRadius: '8px',
      backgroundColor: '#f9f9f9',
      overflow: 'hidden'
    }}>
      <div 
        className="chart-options-header"
        onClick={() => toggleSection('chartOptions')}
        style={{
          padding: '10px 15px',
          cursor: 'pointer',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          borderBottom: expandedSections.chartOptions ? '1px solid #eaeaea' : 'none'
        }}
      >
        <h4 style={{ margin: 0 }}>Display Options</h4>
        <span style={{ transform: `rotate(${expandedSections.chartOptions ? 180 : 0}deg)`, transition: 'transform 0.3s' }}>▼</span>
      </div>
      {expandedSections.chartOptions && (
        <div style={{ padding: '15px' }}>
          <ChartDisplayOptions />
        </div>
      )}
    </div>
  </div>
  );
}

export default ResultsChart;